import { z } from 'zod';
import {
  customListTypes,
  fieldTypeSchema,
  nativeBooleanFieldIds,
  nativeDateTimeFieldIds,
} from './shared-constants';

export const baseFilterDTOSchema = z.object({
  fieldType: fieldTypeSchema,
  viewId: z.number().int().positive(),
  /** Read-only: when this filter was last written. Absent on upserts. */
  updatedAt: z.iso.datetime().optional(),
});

const listFilterBaseSchema = z.object({
  ...baseFilterDTOSchema.shape,
  fieldId: z.string().min(1),
  fieldType: z.enum(customListTypes),
  valueIds: z.array(z.number().int()).min(1).optional(),
  operator: z.enum(['null', 'notnull']).optional(),
});

export const listFilterDTOSchema = listFilterBaseSchema.superRefine((data, ctx) => {
  validateListFilter(data, ctx);
});

export const dateTimeFilterDTOSchema = z
  .object({
    ...baseFilterDTOSchema.shape,
    fieldId: z.enum(nativeDateTimeFieldIds),
    fieldType: z.literal('DATETIME'),
    minTimestamp: z.iso.datetime().optional(),
    maxTimestamp: z.iso.datetime().optional(),
    operator: z.enum(['eq', 'gt', 'lt', 'in', 'null', 'notnull']).optional(),
  })
  .superRefine(validateDateTimeTimestamps);

export type ListFilterDTO = z.infer<typeof listFilterDTOSchema>;
export type DateTimeFilterDTO = z.infer<typeof dateTimeFilterDTOSchema>;

export const booleanFilterDTOSchema = z.object({
  ...baseFilterDTOSchema.shape,
  fieldId: z.enum(nativeBooleanFieldIds as [string, ...string[]]),
  fieldType: z.literal('BOOLEAN'),
  value: z.literal(true),
  operator: z.enum(['eq']),
});

export type BooleanFilterDTO = z.infer<typeof booleanFilterDTOSchema>;

export const booleanFilterUpsertDTOSchema = z
  .object({
    fieldType: z.literal('BOOLEAN'),
    value: z.literal(true),
    operator: z.enum(['eq']),
  })
  .strict();

export const viewFilterDTOSchema = z.discriminatedUnion('fieldType', [
  listFilterDTOSchema,
  dateTimeFilterDTOSchema,
  booleanFilterDTOSchema,
]);

export type FilterDTO = z.infer<typeof viewFilterDTOSchema>;

export const listFilterUpsertDTOSchema = listFilterBaseSchema
  .omit({ fieldId: true, viewId: true, updatedAt: true })
  .strict()
  .superRefine((data, ctx) => {
    validateListFilter(data, ctx);
  });

export const dateTimeFilterUpsertDTOSchema = z
  .object({
    ...baseFilterDTOSchema.omit({ viewId: true, updatedAt: true }).shape,
    fieldType: z.literal('DATETIME'),
    minTimestamp: z.iso.datetime().optional(),
    maxTimestamp: z.iso.datetime().optional(),
    operator: z.enum(['eq', 'gt', 'lt', 'in', 'null', 'notnull']),
  })
  .strict()
  .superRefine(validateDateTimeTimestamps);

function validateListFilter(
  data: {
    operator?: 'null' | 'notnull';
    valueIds?: number[];
  },
  ctx: z.RefinementCtx,
) {
  // operators and valueIds are mutually exclusive. Don't check the operator type as that's handled in the enum validation
  if (data.operator && data.valueIds && data.valueIds.length > 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'Operator cannot be used with other filter values',
      path: ['operator'],
    });
    return;
  }

  if (!data.operator && (!data.valueIds || data.valueIds.length === 0)) {
    ctx.addIssue({
      code: 'custom',
      message: 'Either valueIds or operator must be provided',
      path: ['valueIds', 'operator'],
    });
  }
}

function validateDateTimeTimestamps(
  data: {
    operator?: 'eq' | 'gt' | 'lt' | 'in' | 'null' | 'notnull';
    minTimestamp?: string;
    maxTimestamp?: string;
  },
  ctx: z.RefinementCtx,
) {
  switch (data.operator) {
    case 'null':
    case 'notnull':
      if (data.minTimestamp || data.maxTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'Null/notnull operator cannot be used with other filter values',
          path: ['operator'],
        });
      }
      break;
    case 'gt':
      if (!data.minTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'minTimestamp is required for gt (greater than) operator',
          path: ['minTimestamp'],
        });
      }
      if (data.maxTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'maxTimestamp cannot be used with gt (greater than) operator',
          path: ['maxTimestamp'],
        });
      }
      break;
    case 'lt':
      if (!data.maxTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'maxTimestamp is required for lt (less than) operator',
          path: ['maxTimestamp'],
        });
      }
      if (data.minTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'minTimestamp cannot be used with lt (less than) operator',
          path: ['minTimestamp'],
        });
      }
      break;
    case 'in':
      if (!data.minTimestamp || !data.maxTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'Both minTimestamp and maxTimestamp are required for in operator',
          path: ['minTimestamp', 'maxTimestamp'],
        });
      }
      break;
    case 'eq':
      if (!data.minTimestamp || !data.maxTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'Both minTimestamp and maxTimestamp are required for eq operator',
          path: ['minTimestamp', 'maxTimestamp'],
        });
      }
      break;
  }
}

export const filterUpsertDTOSchema = z.discriminatedUnion('fieldType', [
  listFilterUpsertDTOSchema,
  dateTimeFilterUpsertDTOSchema,
  booleanFilterUpsertDTOSchema,
]);

export type FilterUpsertDTO = z.infer<typeof filterUpsertDTOSchema>;

// Dashboard-scoped filters mirror view-scoped filters but key by dashboardId.
const dashboardListFilterBaseSchema = z.object({
  fieldType: z.enum(customListTypes),
  dashboardId: z.number().int().positive(),
  fieldId: z.string().min(1),
  valueIds: z.array(z.number().int()).min(1).optional(),
  operator: z.enum(['null', 'notnull']).optional(),
});

export const dashboardListFilterDTOSchema = dashboardListFilterBaseSchema.superRefine(
  (data, ctx) => {
    validateListFilter(data, ctx);
  },
);

export const dashboardDateTimeFilterDTOSchema = z
  .object({
    fieldType: z.literal('DATETIME'),
    dashboardId: z.number().int().positive(),
    fieldId: z.enum(nativeDateTimeFieldIds),
    minTimestamp: z.iso.datetime().optional(),
    maxTimestamp: z.iso.datetime().optional(),
    operator: z.enum(['eq', 'gt', 'lt', 'in', 'null', 'notnull']).optional(),
  })
  .superRefine(validateDateTimeTimestamps);

export const dashboardFilterDTOSchema = z.discriminatedUnion('fieldType', [
  dashboardListFilterDTOSchema,
  dashboardDateTimeFilterDTOSchema,
]);

export type DashboardFilterDTO = z.infer<typeof dashboardFilterDTOSchema>;
